import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowLeft,
  Calendar,
  CircleCheck,
  FileText,
  Mail,
  Pause,
  Phone,
  RefreshCw,
  ShieldCheck,
  Square,
  User,
} from 'lucide-react';
import { useI18n, useTranslation } from '@solara/i18n';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Dialog,
  EmptyState,
  useToast,
} from '@solara/ui-shared';
import { useAuth } from '../auth/AuthContext';
import { useTenant, useSuspendTenant } from '@solara/api-client/react';
import { adaptTenant, type AdminTenantRow } from '../data/adapters';
import { LoadingBlock, ErrorBanner } from '../components/QueryState';

export default function TenantDetailPage() {
  const { tenantId } = useParams<{ tenantId: string }>();
  const tenantQuery = useTenant(tenantId);
  const suspend = useSuspendTenant();
  const { locale } = useI18n();
  const { t } = useTranslation('admin');
  const { toast } = useToast();
  const { can, currentUser } = useAuth();
  const [pendingAction, setPendingAction] = useState<'suspend' | 'reactivate' | 'terminate' | null>(null);
  const [reason, setReason] = useState('');

  if (tenantQuery.isLoading) return <LoadingBlock />;
  if (tenantQuery.error) return <ErrorBanner error={tenantQuery.error} />;
  const tenant: AdminTenantRow | undefined = tenantQuery.data ? adaptTenant(tenantQuery.data) : undefined;

  if (!tenant) {
    return (
      <div className="space-y-4">
        <BackLink t={t} />
        <EmptyState icon={<AlertTriangle className="h-6 w-6" />} title="Tenant not found" />
      </div>
    );
  }

  const aedFmt = new Intl.NumberFormat(locale, { style: 'currency', currency: 'AED' });
  const numberFmt = new Intl.NumberFormat(locale);
  const pctFmt = new Intl.NumberFormat(locale, { style: 'percent', minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const dateFmt = new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' });

  async function commitAction() {
    if (!pendingAction || !tenant) return;
    try {
      if (pendingAction === 'suspend') {
        await suspend.mutateAsync({ id: tenant.tenantId, reason: reason || undefined });
        toast({
          title: t('tenants.toast.suspended', { name: tenant.companyNameEn }),
          description: t('tenants.toast.auditWritten', { email: currentUser.email }),
          tone: 'warning',
        });
      } else if (pendingAction === 'reactivate' || pendingAction === 'terminate') {
        toast({
          title:
            pendingAction === 'reactivate'
              ? t('tenants.toast.reactivated', { name: tenant.companyNameEn })
              : t('tenants.toast.terminated', { name: tenant.companyNameEn }),
          description: t('tenants.toast.auditWritten', { email: currentUser.email }),
          tone: pendingAction === 'reactivate' ? 'success' : 'danger',
        });
      }
    } catch (err) {
      toast({
        title: err instanceof Error ? err.message : 'Action failed',
        tone: 'danger',
      });
    }
    setPendingAction(null);
    setReason('');
  }

  return (
    <div className="space-y-6">
      <BackLink t={t} />

      <header className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0 space-y-1">
          <h1 className="text-2xl font-semibold tracking-tight">{tenant.companyNameEn}</h1>
          <p dir="rtl" lang="ar" className="text-fg-muted">{tenant.companyNameAr}</p>
          <div className="flex items-center gap-2 mt-1 text-sm flex-wrap">
            <span className="num text-fg-muted">{t('tenants.detail.trn')}: {tenant.trn}</span>
            <span className="text-fg-subtle">·</span>
            <code className="text-xs text-fg-subtle break-all">{tenant.peppolId}</code>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap shrink-0">
          <StatusBadge status={tenant.onboardingStatus} t={t} />
          {can('tenants.suspend') ? (
            tenant.onboardingStatus === 'ACTIVE' ? (
              <Button variant="outline" size="sm" className="gap-2" onClick={() => setPendingAction('suspend')}>
                <Pause className="h-4 w-4" />
                {t('tenants.detail.actions.suspend')}
              </Button>
            ) : tenant.onboardingStatus === 'SUSPENDED' || tenant.onboardingStatus === 'PENDING' ? (
              <Button size="sm" className="gap-2" onClick={() => setPendingAction('reactivate')}>
                <CircleCheck className="h-4 w-4" />
                {t('tenants.detail.actions.reactivate')}
              </Button>
            ) : null
          ) : null}
          {can('tenants.terminate') && tenant.onboardingStatus !== 'TERMINATED' ? (
            <Button
              variant="outline"
              size="sm"
              className="gap-2 text-danger border-danger/40 hover:bg-danger/5"
              onClick={() => setPendingAction('terminate')}
            >
              <Square className="h-4 w-4" />
              {t('tenants.detail.actions.terminate')}
            </Button>
          ) : null}
          <Button
            variant="ghost"
            size="sm"
            className="gap-2"
            onClick={() =>
              toast({
                title: t('tenants.detail.reverifyToast'),
                description: t('tenants.detail.reverifyToastBody'),
                tone: 'info',
              })
            }
          >
            <RefreshCw className="h-4 w-4" />
            {t('tenants.detail.actions.reverifyTrn')}
          </Button>
          <Button asChild variant="outline" size="sm" className="gap-2">
            <Link to={`/invoices?tenantId=${tenant.tenantId}`}>
              <FileText className="h-4 w-4" />
              {t('tenants.detail.viewInvoicesCta')}
            </Link>
          </Button>
        </div>
      </header>

      <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat label={t('tenants.detail.stats.invoices30d')} value={numberFmt.format(tenant.invoicesYtd)} />
        <Stat label={t('tenants.detail.stats.vat')} value={aedFmt.format(0)} />
        <Stat label={t('tenants.detail.stats.rejection')} value={pctFmt.format(0)} />
        <Stat
          label={t('tenants.detail.stats.nextTdd')}
          value="—"
        />
      </section>

      <section className="grid gap-6 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>{t('tenants.detail.contactTitle')}</CardTitle>
          </CardHeader>
          <CardBody className="grid gap-4 sm:grid-cols-2">
            <Field icon={<User className="h-4 w-4" />} label={t('tenants.detail.primaryContact')} value="—" />
            <Field
              icon={<Mail className="h-4 w-4" />}
              label={t('tenants.detail.email')}
              value={<a href={`mailto:${tenant.contactEmail}`} className="text-brand-700 underline" dir="ltr">{tenant.contactEmail}</a>}
            />
            <Field
              icon={<Phone className="h-4 w-4" />}
              label={t('tenants.detail.phone')}
              value={<span dir="ltr" className="num">{tenant.contactPhone}</span>}
            />
            <Field icon={<ShieldCheck className="h-4 w-4" />} label={t('tenants.detail.mfa')} value={<Badge tone="brand">TOTP</Badge>} />
            <Field label={t('tenants.detail.erp')} value={tenant.industry} />
            <Field label={t('tenants.detail.integration')} value="API" />
          </CardBody>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('tenants.detail.lifecycleTitle')}</CardTitle>
          </CardHeader>
          <CardBody className="space-y-3 text-sm">
            <Row icon={<Calendar className="h-4 w-4 text-fg-subtle" />} label={t('tenants.detail.created')} value={dateFmt.format(new Date(tenant.createdAt))} />
            <Row icon={<Calendar className="h-4 w-4 text-fg-subtle" />} label={t('tenants.detail.activated')} value="—" />
          </CardBody>
        </Card>
      </section>

      <section>
        <Card>
          <CardHeader>
            <CardTitle>{t('tenants.detail.recentTitle')}</CardTitle>
          </CardHeader>
          <CardBody>
            <p className="text-sm text-fg-muted">{t('tenants.detail.recentBody')}</p>
          </CardBody>
        </Card>
      </section>

      <Dialog
        open={!!pendingAction}
        onOpenChange={(o) => {
          if (!o) {
            setPendingAction(null);
            setReason('');
          }
        }}
        title={
          pendingAction === 'suspend'
            ? t('tenants.dialog.suspendTitle', { name: tenant.companyNameEn })
            : pendingAction === 'reactivate'
            ? t('tenants.dialog.reactivateTitle', { name: tenant.companyNameEn })
            : pendingAction === 'terminate'
            ? t('tenants.dialog.terminateTitle', { name: tenant.companyNameEn })
            : ''
        }
        description={
          pendingAction === 'suspend'
            ? t('tenants.dialog.suspendBody')
            : pendingAction === 'reactivate'
            ? t('tenants.dialog.reactivateBody')
            : pendingAction === 'terminate'
            ? t('tenants.dialog.terminateBody')
            : ''
        }
        footer={
          <>
            <Button variant="ghost" onClick={() => setPendingAction(null)}>{t('common.cancel')}</Button>
            <Button onClick={commitAction}>{t('common.confirm')}</Button>
          </>
        }
      >
        <label className="text-sm font-medium text-fg block mb-1.5" htmlFor="tenant-reason">
          {t('tenants.dialog.reasonLabel')}
        </label>
        <textarea
          id="tenant-reason"
          rows={3}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          className="w-full rounded-lg bg-surface border border-border px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/30"
          placeholder={t('tenants.dialog.reasonPlaceholder')}
        />
      </Dialog>
    </div>
  );
}

function BackLink({ t }: { t: (k: string) => string }) {
  return (
    <Link to="/tenants" className="inline-flex items-center gap-1.5 text-sm text-fg-muted hover:text-fg">
      <ArrowLeft className="h-4 w-4 rtl-mirror" />
      {t('tenants.detail.back')}
    </Link>
  );
}

function StatusBadge({
  status,
  t,
}: {
  status: AdminTenantRow['onboardingStatus'];
  t: (k: string) => string;
}) {
  const tone =
    status === 'ACTIVE' ? 'success' : status === 'PENDING' || status === 'SUSPENDED' ? 'warning' : 'danger';
  return <Badge tone={tone}>{t(`tenants.status.${status}`)}</Badge>;
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <Card>
      <CardBody className="p-5 space-y-1">
        <div className="text-xs uppercase tracking-wide text-fg-subtle">{label}</div>
        <div className="text-2xl font-semibold tracking-tight num">{value}</div>
      </CardBody>
    </Card>
  );
}

function Field({ icon, label, value }: { icon?: React.ReactNode; label: string; value: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs uppercase tracking-wide text-fg-subtle flex items-center gap-1.5">
        {icon}
        {label}
      </span>
      <span className="text-sm">{value}</span>
    </div>
  );
}

function Row({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-fg-muted flex items-center gap-2">
        {icon}
        {label}
      </span>
      <span>{value}</span>
    </div>
  );
}
